import ConfirmModal from "@app/components/confirm-modal";
import Table from "@app/components/table";
import { ColumnConfig, Order } from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  SystemMessage,
  TransactionOption,
  TransactionOptionName,
} from "@app/constants";
import TransactionService from "@app/services/http/transaction.service";
import { openPortalDialog } from "@app/services/modal.service";
import { Meta, Transaction } from "@app/types";
import { formatDateTime, formatMoneyVN } from "@app/utils/util";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

function OptionColumnTable({ data }: { data: Transaction }) {
  return (
    <div>
      <span
        className={clsx("px-4 py-2 rounded-lg text-xs", {
          "bg-[#E8FFF4] text-[#00A653]":
            data.status === TransactionOption.SUCCESS,
          "bg-[#FFF0E6] text-[#FE7E20]":
            data.status === TransactionOption.TIMEOUT,
          "bg-[#CCE7FF] text-[#4558FF]":
            data.status === TransactionOption.PROCESSING,
          "bg-[#FFEBEB] text-[#FC4040]":
            data.status === TransactionOption.ERROR,
        })}
      >
        {TransactionOptionName[data.status]}
      </span>
    </div>
  );
}

function CreatedDateColumnTable({ data }: { data: Transaction }) {
  return (
    <div>
      <div>{formatDateTime(data.createdDate)}</div>
    </div>
  );
}

function PriceColumnTable({ data }: { data: Transaction }) {
  return (
    <div>
      <div>{formatMoneyVN(data.price)} đ</div>
    </div>
  );
}

function ActionColumnTable({
  data,
  onClick,
}: {
  data: Transaction;
  onClick: (data: Transaction) => void;
}) {
  const handleClick = () => {
    onClick && onClick(data);
  };

  return (
    <div
      className={clsx(
        "bg-[#5570F1] hover:bg-[#3557ff] select-none text-white rounded-lg cursor-pointer px-4 py-2 w-fit",
        {
          "opacity-[0.6] cursor-default pointer-events-none":
            data.status === TransactionOption.SUCCESS,
        }
      )}
      onClick={handleClick}
    >
      Xác nhận
    </div>
  );
}

const columnConfig: ColumnConfig[] = [
  {
    label: "Thời gian",
    dataKey: "createdDate",
    width: "200px",
    component: CreatedDateColumnTable,
    hasOrder: true,
  },
  {
    label: "Số tiền",
    dataKey: "price",
    width: "220px",
    component: PriceColumnTable,
    hasOrder: true,
  },
  {
    label: "Hình thức",
    dataKey: "formality",
    width: "200px",
    hasOrder: true,
  },
  {
    label: "Nội dung",
    dataKey: "content",
    width: "400px",
    hasOrder: true,
  },
  {
    label: "Trạng thái",
    dataKey: "status",
    component: OptionColumnTable,
    width: "130px",
    theadClassName: "min-w-[130px]",
    hasOrder: true,
  },
  {
    label: "Hành động",
    dataKey: "action",
    component: ActionColumnTable,
    width: "110px",
    theadClassName: "min-w-[110px]",
  },
];

const TransactionTab = () => {
  const { userId } = useParams();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );

  const [meta, setMeta] = useState<Meta>();

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    TransactionService.getPurchasesByUserId(
      Number(userId),
      abortController.signal
    ).then((res) => {
      setTransactions(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleConfirmClick = ({
    data: transaction,
  }: {
    data?: Transaction;
  }) => {
    if (transaction?.purchaseId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.CONFIRM_TRANSACTION_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          TransactionService.confirm(transaction.purchaseId).then(() => {
            addToast({ text: SystemMessage.CONFIRM_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  return (
    <div className=" px-4">
      <div className="rounded-xl bg-white  px-5">
        <TitleDescription
          title="Danh sách danh mục"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={transactions}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            customProps={{
              onClick: handleConfirmClick,
            }}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default TransactionTab;
